import React, { useState, useEffect, useMemo } from "react";
import { useSearch } from "../search/SearchContext";
import { useAuth } from "../auth/AuthContext";
import { FlowStore } from "../flow/FlowTypes";
import { getFlows } from "../../api/flowApi";
import FlowIcon from "../icons/FlowIcons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

function pickIcon(f) {
  if (!f) return "default";
  if (f.icon) return String(f.icon).toLowerCase();
  const name = (f.name || "").toLowerCase();
  const labels = (f.steps || []).map((s) => (s.label || "").toLowerCase()).join(" ");
  if (/invoice|bill|payment/.test(name + " " + labels)) return "invoice";
  if (/bank|account|customer|card|cards|credit/.test(name + " " + labels)) return "bank";
  if (/archive|archive no|archiv/.test(name + " " + labels)) return "archive";
  if (/export|metadata|json|copy/.test(name + " " + labels)) return "export";
  if (/search|find|lookup/.test(name + " " + labels)) return "search";
  return "default";
}

export default function SearchSidebar() {
  const {
    query,
    history,
    saved,
    rows,
    setQuery,
    runSearch,
    clearHistory,
    removeSaved,
    openFlowInCenter,
    centerFlow,
    interpreted,
    lastSearchExpression,
    clearResults,
  } = useSearch();
  const { token } = useAuth();
  const { t } = useTranslation();

  const [flows, setFlows] = useState(() => FlowStore.loadAll());
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // Parse active filters from the parsed filter expression. We deliberately
  // do not fall back to a raw "Query: <text>" chip — if the expression can't
  // be broken down, we show no chips rather than one misleading blob.
  const activeFilters = useMemo(() => {
    const filters = [];
    if (!query?.trim()) return filters;

    const fmtVal = (v) => {
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      const m = s.match(/^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}/);
      return m ? m[1] : s;
    };

    const extractFilters = (node) => {
      if (!node || typeof node !== "object") return;
      for (const [key, val] of Object.entries(node)) {
        if ((key === "$and" || key === "$or") && Array.isArray(val)) {
          val.forEach((clause) => extractFilters(clause));
        } else if (typeof val === "object" && val !== null && !Array.isArray(val)) {
          for (const [op, operand] of Object.entries(val)) {
            const display = fmtVal(operand);
            if (op === "$eq") {
              filters.push({ label: key, value: display });
            } else if (op === "$gte") {
              filters.push({ label: key, value: `≥ ${display}` });
            } else if (op === "$lte") {
              filters.push({ label: key, value: `≤ ${display}` });
            } else if (op === "$gt") {
              filters.push({ label: key, value: `> ${display}` });
            } else if (op === "$lt") {
              filters.push({ label: key, value: `< ${display}` });
            } else if (op === "$like") {
              filters.push({ label: key, value: display.replace(/^%|%$/g, "") });
            } else if (op === "$regex") {
              filters.push({ label: key, value: display });
            } else {
              filters.push({ label: key, value: `${op} ${display}` });
            }
          }
        } else {
          filters.push({ label: key, value: fmtVal(val) });
        }
      }
    };

    const expr = (lastSearchExpression || interpreted || "").trim();
    if (expr) {
      try {
        extractFilters(JSON.parse(expr.replace(/'/g, '"')));
      } catch {
        // No fallback: keep the chip list empty rather than dumping raw text.
      }
    }
    return filters;
  }, [query, interpreted, lastSearchExpression]);

  const clearSearch = () => {
    setQuery("");
    clearResults();
  };

  useEffect(() => {
    if (!token) return;
    getFlows(token)
      .then((serverFlows) => {
        if (Array.isArray(serverFlows) && serverFlows.length > 0) {
          FlowStore.syncFromServer(serverFlows);
          setFlows(serverFlows);
        }
      })
      .catch(() => {});
  }, [token]);

  return (
    <div className="sb-root">
      {/* Context header */}
      <div className="sb-title">{t("sidebar.context")}</div>

      {/* Active Filters */}
      <div className="sb-section">
        <div className="sb-section-title">{t("sidebar.active")}</div>
        {activeFilters.length > 0 ? (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {activeFilters.map((f, i) => (
                <div key={i} className="sb-active-filter">
                  <span className="sb-active-label">{f.label}:</span>
                  <span className="sb-active-value">{f.value}</span>
                </div>
              ))}
            </div>
            <button className="sb-clear-link" onClick={clearSearch}>
              {t("sidebar.clearFilters")}
            </button>
          </>
        ) : (
          <div className="sb-muted">{t("sidebar.noActiveFilters")}</div>
        )}
      </div>

      {/* Recent Searches */}
      <div className="sb-section">
        <div className="sb-section-header">
          <div className="sb-section-title">{t("sidebar.recentSearches")}</div>
          {history.length > 0 && (
            <button className="sb-clear-link" onClick={clearHistory}>
              {t("common.clear")}
            </button>
          )}
        </div>
        {history.length === 0 ? (
          <div className="sb-muted">{t("sidebar.noRecentSearches")}</div>
        ) : (
          <>
            {(historyExpanded ? history : history.slice(0, 4)).map((h, i) => (
              <button
                key={i}
                className="sb-card"
                title={h.q}
                onClick={() => { setQuery(h.q); runSearch(h.q); }}
              >
                <svg className="sb-recent-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                <span className="sb-item-text">{h.q}</span>
              </button>
            ))}
            {history.length > 4 && (
              <button className="sb-view-all" onClick={() => setHistoryExpanded(prev => !prev)}>
                {historyExpanded ? t("sidebar.showLess") : t("sidebar.viewAll")}
              </button>
            )}
          </>
        )}
      </div>

      {/* Saved Views */}
      <div className="sb-section">
        <div className="sb-section-title">{t("sidebar.savedViews")}</div>
        {saved.length === 0 ? (
          <div className="sb-muted">{t("sidebar.noSavedViews")}</div>
        ) : (
          saved.slice(0, 5).map((s, i) => (
            <button
              key={i}
              className="sb-saved-item"
              onClick={() => { setQuery(s.q); runSearch(s.q); }}
              title={s.q}
            >
              <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0, flex: 1 }}>
                <span style={{ fontWeight: s.name !== s.q ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{s.name}</span>
                {s.name !== s.q && (
                  <span style={{ fontSize: 11, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>{s.q}</span>
                )}
              </span>
              <button
                className="sb-remove-btn"
                onClick={(e) => { e.stopPropagation(); removeSaved(s.name); }}
                title={t("sidebar.removeSavedSearch")}
              >
                ×
              </button>
            </button>
          ))
        )}
      </div>

      {/* Search Flows */}
      <div className="sb-section">
        <div className="sb-section-title">{t("sidebar.workflows")}</div>
        {flows && flows.length > 0 ? (
          flows.slice(0, 8).map((f) => (
            <button
              key={f.name}
              className="sb-workflow-item"
              onClick={() => openFlowInCenter && openFlowInCenter(f.name)}
              title={t("sidebar.runFlow")}
            >
              <FlowIcon name={pickIcon(f)} className="sb-wf-icon" />
              <span className="sb-wf-label">{f.name}</span>
              <span className="sb-wf-run">&#9654;</span>
              <span className="sb-wf-arrow">&rsaquo;</span>
            </button>
          ))
        ) : (
          <div className="sb-muted">{t("sidebar.noFlows")}</div>
        )}
        <Link to="/flow-designer" className="sb-add-workflow">
          <span style={{ fontSize: 16 }}>+</span>
          {t("sidebar.newWorkflow")}
        </Link>
      </div>
    </div>
  );
}
